<script>
import ProfileLayout from "@/Layouts/ProfileLayout.vue";

export default {
    layout: ProfileLayout,
};
</script>

<script setup>
import { computed } from "vue";
import { Head, Link, router, usePage } from "@inertiajs/vue3";
import ConfirmedGiftsTabs from "@/Components/Profile/ConfirmedGiftsTabs.vue";
import GiftCard from "@/Components/Profile/GiftCard.vue";
import ListsPagination from "@/Components/Profile/ListsPagination.vue";
import ProfileHero from "@/Components/Profile/ProfileHero.vue";
import ProfileTabs from "@/Components/Profile/ProfileTabs.vue";

// Access props through the page object so in-place reloads aren't read from
// a stale props reference.
const inertiaPage = usePage();

const owner = computed(() => inertiaPage.props.owner?.data);
const items = computed(() => inertiaPage.props.items?.data ?? []);
const meta = computed(() => inertiaPage.props.items?.meta);

// GiftCard hides deleted items, but a purchase stays part of your history
// even after the item or its list is removed — those get a placeholder card.
const liveItems = computed(() =>
    items.value.filter((item) => !item.is_deleted),
);
const deletedItems = computed(() =>
    items.value.filter((item) => item.is_deleted),
);

function confirmationLabel(item) {
    return item.user
        ? `Confirmed for ${item.user.presentation_name}`
        : "Confirmed by you";
}

function goToPage(n) {
    router.reload({
        data: { page: n },
        only: ["items"],
        preserveState: true,
    });
}
</script>

<template>
    <div>
        <Head>
            <title>Confirmed Gifts | Gift Hero</title>
        </Head>

        <ProfileHero
            :owner="owner"
            :is-owner="true"
            :theme="owner.profile_theme"
        />

        <ProfileTabs :owner="owner" :is-owner="true" active="confirmed" />

        <div class="px-4 pb-8 sm:px-[35px] sm:pb-9">
            <section
                class="rounded-brand bg-brand-cream px-4 py-6 sm:px-[46px] sm:py-8"
            >
                <header
                    class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
                >
                    <h2 class="font-brand text-[25px] font-bold text-[#102a3d]">
                        Confirmed Gifts
                    </h2>
                    <ConfirmedGiftsTabs active="by-me" />
                    <!-- Spacer keeps the toggle centered like the For Me view -->
                    <span class="hidden w-[220px] lg:block"></span>
                </header>

                <template v-if="items.length">
                    <p class="mt-4 font-brand text-[15px] text-[#102a3d]">
                        Well aren&rsquo;t you thoughtful! These are the gifts
                        you&rsquo;ve confirmed on Gift Hero.
                    </p>

                    <div
                        class="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    >
                        <GiftCard
                            v-for="item in liveItems"
                            :key="item.uuid"
                            :item="item"
                            :list="{}"
                            :spoiled="true"
                            :is-owner="false"
                            :confirmed="true"
                            :confirmation-label="confirmationLabel(item)"
                        />

                        <article
                            v-for="item in deletedItems"
                            :key="item.uuid"
                            class="flex flex-col rounded-[4px] bg-white"
                        >
                            <div class="m-[15px] mb-0">
                                <div
                                    class="grid aspect-[219/154] w-full place-items-center overflow-hidden rounded-[2px] bg-[#f4f4f6]"
                                >
                                    <img
                                        v-if="
                                            item.image_url?.img_2x ||
                                            item.image_url?.img_1x
                                        "
                                        :src="
                                            item.image_url?.img_2x ??
                                            item.image_url?.img_1x
                                        "
                                        :alt="item.name"
                                        class="size-full object-cover opacity-50 grayscale"
                                    />
                                </div>
                            </div>
                            <div
                                class="flex min-w-0 flex-1 flex-col px-4 pb-3 pt-3.5"
                            >
                                <p
                                    class="line-clamp-2 break-words font-brand text-[18px] font-semibold leading-[1.3] text-brand-navy/60 max-lg:text-[22px]"
                                >
                                    {{ item.name }}
                                </p>
                                <p
                                    class="mt-1 truncate font-brand text-[11px] font-semibold text-[#8487a1] max-lg:text-[13px]"
                                >
                                    {{ confirmationLabel(item) }} &middot; no
                                    longer available
                                </p>
                            </div>
                        </article>
                    </div>

                    <ListsPagination
                        v-if="meta?.last_page > 1"
                        :page="meta.current_page"
                        :pages="meta.last_page"
                        class="mt-10"
                        @update:page="goToPage"
                    />
                </template>

                <div
                    v-else
                    class="flex flex-col items-center gap-5 py-14 text-center"
                >
                    <p class="max-w-md font-brand text-[16px] text-[#565a77]">
                        It looks like you haven&rsquo;t confirmed any gifts yet.
                        When you confirm gifts for
                        <Link
                            :href="route('friends.index')"
                            class="font-semibold text-brand-red hover:underline"
                            >friends</Link
                        >
                        on Gift Hero, they&rsquo;ll appear here.
                    </p>
                </div>
            </section>
        </div>
    </div>
</template>
